/**
 * Render a short preview of a TODO's body text for surfaces that
 * can't afford the full multi-line summary (the list-row subtitle,
 * the drawer's ``SheetDescription``).
 *
 * Preference order:
 *   1. First sentence-like chunk — split on the first ``. ``, ``? ``,
 *      ``! `` or newline. Strips trailing whitespace.
 *   2. If that chunk is still longer than ``maxChars``, fall back to a
 *      character-bounded ellipsis cut.
 *   3. If there's no sentence break at all, char-cap with ellipsis.
 *
 * Whitespace collapses to single spaces before measurement so a
 * raw textarea with ``\n\n`` between paragraphs reads naturally on
 * a single line. Returns ``""`` for null / empty input (callers
 * should treat empty preview the same as "no subtitle").
 */
// Sentence-like break detection. The newline arm must run against
// the (HTML-stripped) text before whitespace collapse — collapsing
// ``\s+`` to a single space first would erase the newline boundary.
const _SENTENCE_END_RE = /([.!?])\s+|\n+/;

// Tag-strip + entity-decode pre-pass. Ninety-imported manual TODOs
// store their body as sanitized HTML (``<ul><li>…</li></ul>``); the
// preview surfaces render plain text (the list-row subtitle, the
// drawer's ``SheetDescription``), so we MUST normalize HTML to text
// before sentence-detection. Without this, a body like
// ``"<ul>\n<li>Follow up</li>\n</ul>"`` previews as ``"<ul>…"`` — the
// newline arm of ``_SENTENCE_END_RE`` fires at position 4 (right
// after the opening tag) and the rest is dropped.
//
// Regex-based rather than DOM-based because (a) the preview is hot —
// fires per row on every list render — and (b) the source is already
// DOMPurify-sanitized upstream (no event handlers / script tags to
// worry about), so a bare tag strip is enough. Block-level tags
// become a space rather than empty so ``"<p>One</p><p>Two</p>"``
// reads as ``"One Two"``, not ``"OneTwo"``; the whitespace collapse
// in the chosen chunk trims the doubled-space artifact. A quoted attribute
// value is skipped whole, so a ``>`` inside one (``title="cost > $500"``)
// does not end the tag early and leak the rest of it as text.
const _HTML_TAG_RE = /<\/?[a-zA-Z](?:[^>"']|"[^"]*"|'[^']*')*>/g;
const _HTML_NAMED_ENTITY_MAP: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};
// Every entity form, decoded in ONE pass so a decoded ``&`` never starts a
// second entity (``&amp;lt;`` reads ``&lt;``, not ``<``).
const _HTML_ENTITY_RE = /&(?:#(\d+)|#x([0-9a-f]+)|[a-z][a-z0-9]*);/gi;
const _namedEntityCache = new Map<string, string>();

/** A named entity outside the common map (``&mdash;``, ``&eacute;``): decoded
 *  by the browser through an inert ``<textarea>`` (its content is text, so
 *  nothing parses or runs), cached per entity. Unknown names stay as written. */
function _decodeNamedEntity(entity: string): string {
  const known = _HTML_NAMED_ENTITY_MAP[entity] ?? _namedEntityCache.get(entity);
  if (known !== undefined) return known;
  if (typeof document === "undefined") return entity;
  const box = document.createElement("textarea");
  box.innerHTML = entity;
  _namedEntityCache.set(entity, box.value);
  return box.value;
}

function _decodeEntities(input: string): string {
  return input.replace(_HTML_ENTITY_RE, (m, dec?: string, hex?: string) => {
    if (dec === undefined && hex === undefined) return _decodeNamedEntity(m);
    const n = dec !== undefined ? parseInt(dec, 10) : parseInt(hex as string, 16);
    return Number.isFinite(n) && n > 0 && n < 0x10ffff ? String.fromCodePoint(n) : "";
  });
}

function _stripHtml(input: string): string {
  // Replace tags with a space (not empty) so adjacent block-level
  // elements don't collide their text content.
  return _decodeEntities(input.replace(_HTML_TAG_RE, " "));
}

// Editor seed only. ``/g`` is omitted on the detector: ``RegExp.test``
// advances ``lastIndex`` and would skip the next note.
const _HAS_HTML_TAG_RE = /<\/?[a-zA-Z][^>]*>/;
const _HTML_LI_OPEN_RE = /<li\b[^>]*>/gi;
const _HTML_BR_RE = /<br\s*\/?>/gi;
// ``</li>`` is not a closer here: the next ``<li>`` already starts a line,
// and turning both into newlines inserts a blank line between items.
const _HTML_BLOCK_CLOSE_RE = /<\/(?:p|div|h[1-6]|tr|ul|ol|blockquote)\s*>/gi;

/**
 * Notes text for the editor. ``htmlToText`` collapses every newline into
 * a space, which is right for a one-line preview and wrong here: a saved
 * ``"- "`` list comes back as one paragraph, so the bullets never
 * re-render. Plain markdown (and any note with no tags) keeps its line
 * breaks. Ninety HTML becomes markdown-ish lines (``<li>`` → ``"- "``)
 * so the editor shows a list instead of raw tags.
 */
export function notesForEditor(input: string | null | undefined): string {
  if (!input) return "";
  if (!_HAS_HTML_TAG_RE.test(input)) return _decodeEntities(input);
  const structured = input
    .replace(_HTML_LI_OPEN_RE, "\n- ")
    .replace(_HTML_BR_RE, "\n")
    .replace(_HTML_BLOCK_CLOSE_RE, "\n");
  // Remaining tags (openers, ``</li>``, inline emphasis) drop out. The
  // line breaks above already separate blocks, so a space-for-tag
  // replacement would pad each bullet.
  return _decodeEntities(structured.replace(_HTML_TAG_RE, ""))
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Full HTML → single-line plain text: strip tags, decode entities, and
 * collapse whitespace. For compact PREVIEW surfaces that render the whole
 * (visually clamped) body as text — e.g. the lead-card description and the
 * blocked-TODO callout — where third-party notes (ServiceTitan / Pipedrive)
 * arrive as HTML and must not show raw tags. For sentence-truncated previews
 * (list-row subtitles) use `summaryPreview` instead.
 */
export function htmlToText(input: string | null | undefined): string {
  if (!input) return "";
  return _stripHtml(input).replace(/\s+/g, " ").trim();
}

// Regex patterns for markdown token stripping.
// Order matters: headings first (so `##` doesn't leave a lone `#`), then
// emphasis/bold/strikethrough, then inline code, then links, then images.
const _MD_HEADING_RE = /^#{1,6}\s+/gm;
// Emphasis stripping uses CommonMark-ish flanking rules to avoid corrupting plain text:
//
//  * / ** / ***: opener must NOT be preceded by another * (avoids splitting **) AND must
//    NOT be followed by whitespace (so "5 * 3" is arithmetic, not emphasis). Closer must
//    NOT be preceded by whitespace, must NOT be preceded by * (avoids eating the first *
//    of ** as a closer for a single-* opener — the root cause of "*args and **kwargs"
//    corruption), and must NOT be followed by another * (same count check via \1).
//
//  _ / __ / ___: opener must NOT be preceded by a word character (so intraword underscores
//    in snake_case identifiers and digit-group separators like 1_000_000 are left alone)
//    AND must NOT be followed by whitespace. Closer must NOT be preceded by whitespace and
//    must NOT be followed by a word character (so the closing _ of a snake_case tail is
//    never mistaken for a closer).
const _MD_STAR_RE = /(?<!\*)(\*{1,3})(?!\s)([\s\S]*?)(?<!\s)(?<!\*)\1(?!\*)/g;
const _MD_UNDER_RE = /(?<!\w)(_{1,3})(?!\s)([\s\S]*?)(?<!\s)\1(?!\w)/g;
const _MD_STRIKETHROUGH_RE = /~~(.*?)~~/gs;
const _MD_INLINE_CODE_RE = /`([^`\n]+)`/g;
const _MD_LINK_RE = /!?\[([^\]]*)\]\([^)]*\)/g;
const _MD_BLOCKQUOTE_RE = /^>\s?/gm;
const _MD_HR_RE = /^[-*_]{3,}\s*$/gm;
const _MD_LIST_MARKER_RE = /^(\s*)([-*+]|\d+\.)\s+/gm;

/**
 * Strip markdown syntax tokens → plain text for clamped-preview surfaces
 * (line-clamp rows, row subtitles) where block markdown must not render.
 *
 * Preserves the underlying text content; only removes the punctuation that
 * marks formatting (asterisks, underscores, backticks, heading `#`, etc.).
 * Does NOT collapse newlines so `line-clamp` can still see paragraph
 * boundaries if the caller needs the first-line extraction.
 *
 * For sentence-truncated row subtitles, chain with `summaryPreview`.
 * For HTML-carrying fields (ServiceTitan / Ninety imports), use `htmlToText`.
 */
export function stripMarkdown(input: string | null | undefined): string {
  if (!input) return "";
  return input
    .replace(_MD_HEADING_RE, "")
    .replace(_MD_STAR_RE, "$2")
    .replace(_MD_UNDER_RE, "$2")
    .replace(_MD_STRIKETHROUGH_RE, "$1")
    .replace(_MD_INLINE_CODE_RE, "$1")
    .replace(_MD_LINK_RE, "$1")
    .replace(_MD_BLOCKQUOTE_RE, "")
    .replace(_MD_HR_RE, "")
    .replace(_MD_LIST_MARKER_RE, "$1")
    .trim();
}

export function summaryPreview(text: string | null | undefined, maxChars = 160): string {
  if (!text) return "";
  // Normalize HTML → plain text BEFORE sentence-detection. Ninety-
  // imported rows carry HTML bodies; their newlines used to truncate
  // the preview at the opening tag (e.g. ``"<ul>…"``). Manual /
  // free-text TODOs pass through this pre-pass with no effect (no
  // tags to strip, no entities to decode).
  const trimmed = _stripHtml(text).trim();
  if (!trimmed) return "";

  // Find the first sentence / line break in the RAW (newline-preserving)
  // text. Splitting here ensures a multi-line manual TODO ("First line\n
  // Second line") previews on the first line rather than concatenating
  // them with a space (which collapsing whitespace first would do).
  const match = _SENTENCE_END_RE.exec(trimmed);
  let firstChunk: string;
  let hasMore: boolean;
  if (match) {
    // ``RegExp.exec`` index points at the start of the matched
    // separator; include the punctuation (if any) but drop the
    // separator's whitespace.
    const end = match.index + (match[1] ? 1 : 0);
    firstChunk = trimmed.slice(0, end).trim();
    hasMore = trimmed.length > match.index + match[0].length;
  } else {
    firstChunk = trimmed;
    hasMore = false;
  }

  // Now collapse internal whitespace in the chosen chunk so it
  // renders on a single line in the list-row / drawer subtitle.
  firstChunk = firstChunk.replace(/\s+/g, " ");

  if (firstChunk.length <= maxChars) {
    // Sentence is short enough — emit as-is. If the original text
    // had more after the first sentence, append an ellipsis hint so
    // the rep knows there's more in the dialog / full surface.
    return hasMore ? `${firstChunk}…` : firstChunk;
  }

  // First sentence was already too long — hard-cut at maxChars - 1 and
  // append the ellipsis. Trim trailing partial-word characters back
  // to the last whitespace so we don't cut a word in half.
  const hardCut = firstChunk.slice(0, maxChars - 1);
  const lastSpace = hardCut.lastIndexOf(" ");
  const cutAt = lastSpace > maxChars * 0.7 ? lastSpace : hardCut.length;
  return `${hardCut.slice(0, cutAt).trim()}…`;
}
